import React, { FunctionComponent, useMemo } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import type { SxProps, Theme } from "@mui/material/styles";

import { IconWithBackground } from "@/components/IconWithBackground/IconWithBackground";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { Info } from "@/icons/outlined";

interface PdfPreviewModalProps {
  onClose: () => void;
  open: boolean;
  title?: string;
  headerIconTooltip?: React.ReactNode;
  contentSx?: SxProps<Theme>;
}

export const PdfPreviewModal: FunctionComponent<React.PropsWithChildren<PdfPreviewModalProps>> = ({
  children,
  contentSx,
  headerIconTooltip,
  onClose,
  open,
  title,
}) => {
  const renderIcon = useMemo(() => {
    if (headerIconTooltip === undefined || headerIconTooltip === null || headerIconTooltip === "") {
      return undefined;
    }
    return () => (
      <DashboardTooltip title={headerIconTooltip} placement="bottom">
        <Box component="span" sx={{ display: "inline-flex", cursor: "help" }}>
          <IconWithBackground vibe="brand" icon={<Info />} />
        </Box>
      </DashboardTooltip>
    );
  }, [headerIconTooltip]);

  return (
    <DashboardDialog open={open} onClose={onClose} renderIcon={renderIcon} title={title} maxWidth="md" fullWidth>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        alignContent="center"
        sx={
          [
            {
              height: "60vh",
              ["@supports (height: 60dvh)"]: {
                height: "60dvh",
              },
            },
            ...(contentSx ? [contentSx] : []),
          ] as SxProps<Theme>
        }
      >
        {children}
      </Grid>
    </DashboardDialog>
  );
};
